import { expandProfileQuestionBlocks } from './profileQuestionBlocks';
import type { NormalizedChatMessage } from './normalizeApiMessage';
import { resolveQuestionFromMessage } from './questionMessage';

export type QuestionThreadRef = {
  id: string;
  questionText: string;
  answerText?: string;
  answerAttachments?: NormalizedChatMessage['attachments'];
  section?: string;
  questionItems?: Array<{ section?: string; questionText?: string; question?: string }>;
};

export const QUESTION_ANSWER_PLACEHOLDER_PREFIX = 'question-answer-';

export function isPlaceholderQuestionAnswerId(id: string | number): boolean {
  return String(id).startsWith(QUESTION_ANSWER_PLACEHOLDER_PREFIX);
}

export function sortThreadMessages(messages: NormalizedChatMessage[]): NormalizedChatMessage[] {
  return [...messages].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
}

/** Drop optimistic answer placeholders; dedupe by message id. */
export function sanitizeQuestionThreadMessages(
  messages: NormalizedChatMessage[]
): NormalizedChatMessage[] {
  const byId = new Map<string, NormalizedChatMessage>();
  for (const message of messages) {
    if (isPlaceholderQuestionAnswerId(message.id)) {
      continue;
    }
    byId.set(String(message.id), message);
  }
  return sortThreadMessages(Array.from(byId.values()));
}

function isDuplicateQuestionMessage(
  message: NormalizedChatMessage,
  question: QuestionThreadRef
): boolean {
  const parsed = resolveQuestionFromMessage(message);
  if (!parsed) return false;
  const blockTexts = expandProfileQuestionBlocks({
    section: question.section,
    questionText: question.questionText,
    questionItems: question.questionItems,
  }).map((block) => block.questionText);
  return blockTexts.includes(parsed.questionText);
}

export function isQuestionThreadMessage(
  message: NormalizedChatMessage,
  question: QuestionThreadRef
): boolean {
  return String(message.chatRequestId || '') === String(question.id);
}

export function isQuestionAnswerThreadMessage(
  message: NormalizedChatMessage,
  question: QuestionThreadRef,
  isCandidatePredicate: (message: NormalizedChatMessage) => boolean,
  threadMessages: NormalizedChatMessage[]
): boolean {
  if (!isQuestionThreadMessage(message, question) || !isCandidatePredicate(message)) {
    return false;
  }
  if (isDuplicateQuestionMessage(message, question)) {
    return false;
  }
  const text = (message.content || '').trim();
  const answerText = (question.answerText || '').trim();
  if (answerText && text === answerText) {
    return true;
  }
  if (!text && message.attachments?.length && question.answerAttachments?.length) {
    return true;
  }
  const firstCandidateReply = threadMessages.find(
    (row) =>
      isQuestionThreadMessage(row, question) &&
      isCandidatePredicate(row) &&
      !isDuplicateQuestionMessage(row, question)
  );
  return firstCandidateReply?.id === message.id;
}

/** Messages scoped to a single profile question thread (answer + follow-ups). */
export function filterQuestionThreadMessages(
  messages: NormalizedChatMessage[],
  question: QuestionThreadRef,
  isCandidateMessage: (message: NormalizedChatMessage) => boolean
): NormalizedChatMessage[] {
  const questionId = String(question.id);
  return messages.filter((message) => {
    if (String(message.chatRequestId || '') !== questionId) {
      return false;
    }
    if (isDuplicateQuestionMessage(message, question)) {
      return false;
    }
    return true;
  });
}

export function hubQuestionToThreadRef(question: {
  id: string;
  question: string;
  reply?: string;
  replyAttachments?: Array<{
    id?: string;
    fileName?: string;
    fileUrl: string;
    type?: string;
    mimeType?: string;
  }>;
  section?: string;
  questionItems?: QuestionThreadRef['questionItems'];
}): QuestionThreadRef {
  return {
    id: question.id,
    questionText: question.question,
    answerText: question.reply,
    answerAttachments: question.replyAttachments?.map((file, index) => ({
      id: file.id || String(index),
      fileName: file.fileName || 'attachment',
      fileUrl: file.fileUrl,
      type: file.type || 'file',
      mimeType: file.mimeType || file.type || 'application/octet-stream',
    })),
    section: question.section,
    questionItems: question.questionItems,
  };
}

export function isFirstCandidateTaggedReply(
  message: NormalizedChatMessage,
  all: NormalizedChatMessage[],
  candidateUserId?: number
): boolean {
  if (!message.chatRequestId) return false;

  const isCandidate = (row: NormalizedChatMessage) =>
    candidateUserId
      ? Number(row.senderId) === Number(candidateUserId)
      : row.sender?.role === 'candidate';

  if (!isCandidate(message)) return false;

  const tagged = sortThreadMessages(
    all.filter(
      (row) =>
        String(row.chatRequestId) === String(message.chatRequestId) &&
        !resolveQuestionFromMessage(row)
    )
  );

  const firstFromCandidate = tagged.find((row) => isCandidate(row));
  return firstFromCandidate?.id === message.id;
}
